import { useEffect, useState } from 'react';
import { Handshake, ShieldAlert, ShieldCheck, X, BadgeCheck, Users, AlertTriangle, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { communityTrust } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * Community Trust & Safety panel for a store's public detail page.
 *
 * - Displays the store's Trust Score card with breakdown, days active,
 *   vouches, and open reports.
 * - Shows the wall-license photo (social proof) if the merchant uploaded one.
 * - "I've bought here — vouch" button (customers). 3 vouches promote a
 *   private new store to `is_public=true`.
 * - "Report Store" button. 3 `bad_quality/fake_products` reports auto-suspend
 *   the store platform-wide.
 */
export default function CommunityTrustPanel({ store, onStoreUpdated }) {
  const { user } = useAuth();
  const [myStatus, setMyStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isOwnStore = user && store?.owner_id === user.id;

  useEffect(() => {
    if (!store?.id) return;
    communityTrust.reportsSummary(store.id).then(setSummary).catch(() => {});
    if (user && !isOwnStore) {
      communityTrust.myStatus(store.id).then(setMyStatus).catch(() => {});
    }
  }, [store?.id, user, isOwnStore]);

  const doVouch = async () => {
    if (!user) { toast.error('Please sign in to vouch for this shop'); return; }
    setBusy(true);
    try {
      const r = await communityTrust.vouch(store.id);
      toast.success(r.promoted_to_public
        ? `🎉 ${store.name} is now public — 3 vouches reached!`
        : `Thanks for vouching (${r.vouch_count}/3)`);
      setMyStatus({ ...(myStatus || {}), has_vouched: true });
      onStoreUpdated?.({ ...store, vouch_count: r.vouch_count, is_public: r.is_public });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not vouch');
    } finally { setBusy(false); }
  };

  const trust = store.trust || {};
  const tier = trust.tier || 'new';
  const tierColor = {
    gold:      'bg-[#FFF3E0] text-[#B26900] border-[#FFCC80]',
    silver:    'bg-[#ECEFF1] text-[#37474F] border-[#B0BEC5]',
    bronze:    'bg-[#FBE9E7] text-[#8D3A0A] border-[#FFAB91]',
    new:       'bg-[#E3F2FD] text-[#0D47A1] border-[#90CAF9]',
    suspended: 'bg-[#FFEBEE] text-[#B71C1C] border-[#EF9A9A]',
  }[tier];

  const isSuspended = store.status === 'suspended';
  const criticalOpen = summary?.critical_open ?? 0;
  const critThreshold = summary?.critical_threshold ?? 3;
  const vouchCount = store.vouch_count ?? trust.vouches ?? 0;
  const vouchThreshold = 3;
  const daysActive = trust.days_active ?? 0;

  return (
    <section data-testid="community-trust-panel" className="card-soft p-5 mb-6">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-[#1B5E20]"/>
        <div className="font-heading font-bold text-lg">Community Trust</div>
        <span className={`ml-auto inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tierColor}`}>
          {isSuspended ? <><Ban className="w-3 h-3"/>Suspended</> : <><BadgeCheck className="w-3 h-3"/>{tier} · {trust.score ?? '—'}/100</>}
        </span>
      </div>

      {isSuspended && (
        <div data-testid="store-suspended-banner" className="mt-3 rounded-lg bg-[#FFEBEE] border border-[#FFCDD2] p-3 text-sm text-[#B71C1C]">
          <div className="flex items-center gap-2 font-bold"><AlertTriangle className="w-4 h-4"/>This store is temporarily suspended</div>
          <div className="mt-1 text-xs text-[#B71C1C]/80">Auto-suspended after {critThreshold} customer reports. Admin is reviewing. New orders are disabled.</div>
        </div>
      )}

      {/* Vouch progress — visible only while store is not yet public */}
      {!store.is_public && !isSuspended && (
        <div data-testid="vouch-progress" className="mt-4 rounded-lg bg-[#E8F5E9] border border-[#A5D6A7] p-3">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#1B5E20] inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5"/>New shop · {vouchCount}/{vouchThreshold} vouches to go public
          </div>
          <div className="mt-2 h-2 rounded-full bg-white overflow-hidden border border-[#A5D6A7]">
            <div
              data-testid="vouch-progress-bar"
              className="h-full bg-[#1B5E20] transition-all"
              style={{ width: `${Math.min(100, (vouchCount / vouchThreshold) * 100)}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-ink/70">
            {vouchCount >= vouchThreshold
              ? '✅ Reached threshold — this shop is publicly listed.'
              : `${vouchThreshold - vouchCount} more vouch${(vouchThreshold - vouchCount) === 1 ? '' : 'es'} from existing users will make this shop public.`}
          </div>
        </div>
      )}

      {/* Vouch & report buttons */}
      {!isOwnStore && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            data-testid="vouch-button"
            onClick={doVouch}
            disabled={busy || myStatus?.has_vouched || isSuspended}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1B5E20] hover:bg-[#0D3F12] disabled:bg-ink/20 text-white px-4 h-9 text-xs font-bold uppercase tracking-wider transition"
          >
            <Handshake className="w-3.5 h-3.5"/>
            {myStatus?.has_vouched ? "You've vouched ✓" : "I've bought here — vouch"}
          </button>
          <button
            data-testid="report-store-button"
            onClick={() => setReportOpen(true)}
            disabled={myStatus?.has_reported}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#B71C1C] hover:bg-[#FFEBEE] disabled:opacity-60 text-[#B71C1C] px-4 h-9 text-xs font-bold uppercase tracking-wider transition"
          >
            <ShieldAlert className="w-3.5 h-3.5"/>
            {myStatus?.has_reported ? 'Report submitted ✓' : 'Report store'}
          </button>
        </div>
      )}

      {/* Public transparency: report counter (never PII) */}
      {summary && summary.total_open > 0 && !isSuspended && (
        <div data-testid="report-counter" className="mt-3 rounded-lg bg-[#FFF3E0] border border-[#FFCC80] p-2.5 text-xs text-[#9A4500]">
          <b>{summary.total_open}</b> open customer reports · <b>{criticalOpen}/{critThreshold}</b> critical (quality/fake).
          {criticalOpen >= critThreshold - 1 && (
            <span className="ml-1 font-bold">Warning: {critThreshold - criticalOpen} more critical reports will auto-suspend this shop.</span>
          )}
        </div>
      )}

      {/* Trust score breakdown */}
      {trust.breakdown && (
        <details data-testid="trust-breakdown" className="mt-4 group">
          <summary className="cursor-pointer text-[11px] uppercase tracking-[0.18em] font-bold text-ink/60 hover:text-ink">
            How this trust score is calculated ▾
          </summary>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
            {Object.entries(trust.breakdown).map(([k, v]) => (
              <div key={k} className="rounded-lg bg-muted px-2 py-1.5 flex items-center justify-between">
                <span className="uppercase text-ink/55 tracking-wider">{k.replace(/_/g, ' ')}</span>
                <span className={`font-bold ${v < 0 ? 'text-[#B71C1C]' : 'text-ink'}`}>{v > 0 ? `+${v}` : v}</span>
              </div>
            ))}
            <div className="rounded-lg bg-[#E8F5E9] border border-[#A5D6A7] px-2 py-1.5 flex items-center justify-between">
              <span className="uppercase text-[#1B5E20] tracking-wider">Days active</span>
              <span className="font-bold text-[#1B5E20]">{daysActive}d</span>
            </div>
          </div>
          <div className="mt-2 text-[10px] text-ink/50">
            Trust builds automatically — longer active time + community vouches + verified KYC + wall-license photo. Open customer reports subtract from it.
          </div>
        </details>
      )}

      {reportOpen && (
        <ReportStoreDialog
          store={store}
          onClose={() => setReportOpen(false)}
          onReported={(r) => {
            setMyStatus({ ...(myStatus || {}), has_reported: true });
            if (r.auto_suspended) onStoreUpdated?.({ ...store, status: 'suspended', is_public: false });
            communityTrust.reportsSummary(store.id).then(setSummary).catch(() => {});
          }}
        />
      )}
    </section>
  );
}

export function ReportStoreDialog({ store, onClose, onReported }) {
  const [category, setCategory] = useState('bad_quality');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [busy, setBusy] = useState(false);

  const CATS = [
    ['bad_quality', 'Bad quality / spoiled goods'],
    ['fake_products', 'Fake / counterfeit products'],
    ['expired_items', 'Expired items sold'],
    ['overcharging', 'Overcharging beyond MRP'],
    ['misbehaviour', 'Rude / misbehaviour with customers'],
    ['hygiene', 'Poor hygiene / unsafe premises'],
    ['other', 'Other (explain below)'],
  ];

  const submit = async () => {
    if (reason.trim().length < 8) { toast.error('Please describe the issue (min 8 chars)'); return; }
    setBusy(true);
    try {
      const r = await communityTrust.report(store.id, {
        category, reason: reason.trim(),
        evidence_url: evidence.trim() || undefined,
      });
      if (r.auto_suspended) {
        toast.success(`⚠️ ${store.name} has been auto-suspended.`, {
          description: `${r.critical_count} critical reports reached the threshold.`,
        });
      } else {
        toast.success('Report submitted. Admin will review it.');
      }
      onReported?.(r);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit report');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div data-testid="report-store-dialog" className="bg-white w-full max-w-md rounded-2xl p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#B71C1C]">
              <ShieldAlert className="w-3.5 h-3.5"/>Report store
            </div>
            <h3 className="font-heading font-bold text-xl mt-1">{store.name}</h3>
            <p className="text-xs text-ink/60 mt-1">
              3 reports for <b>bad quality</b> or <b>fake products</b> auto-suspend a store pending admin review. Be honest — false reports may attract action against your account.
            </p>
          </div>
          <button onClick={onClose} className="text-ink/40 hover:text-ink"><X className="w-5 h-5"/></button>
        </div>

        <div className="mt-4 space-y-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Reason</div>
            <select data-testid="report-store-category" value={category} onChange={e => setCategory(e.target.value)} className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none">
              {CATS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">What happened?</div>
            <textarea data-testid="report-store-reason" rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Describe what went wrong (min 8 characters)" className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Evidence URL (optional)</div>
            <input data-testid="report-store-evidence" value={evidence} onChange={e => setEvidence(e.target.value)} placeholder="Photo / video URL" className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none"/>
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary !py-2 !text-sm">Cancel</button>
          <button data-testid="report-store-submit" disabled={busy} onClick={submit} className="btn-primary !py-2 !text-sm">
            {busy ? 'Submitting…' : 'Submit report'}
          </button>
        </div>
      </div>
    </div>
  );
}
